import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { CONVERSATION_THREAD_COMMENT_ADD } from '../../../utils/apollo/conversations';
import {
  ICommentInput,
  IConversationEntityFieldsFragment,
  IConversationThreadCommentAddMutation,
  IConversationThreadCommentAddMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { assertNotNil } from '../../../utils/types';
import { useAppDispatch } from '../../../redux/hooks';
import { updateThread } from '../../../redux/slices/conversationThreads';

const useAddThreadComment = (
  entity: IConversationEntityFieldsFragment,
  threadId: string,
) => {
  const dispatch = useAppDispatch();

  const handleMutationComplete = ({
    conversationThreadCommentAdd,
  }: IConversationThreadCommentAddMutation) => {
    if (printGraphqlErrors(conversationThreadCommentAdd.errors)) {
      return;
    }
    const thread = conversationThreadCommentAdd.thread;

    assertNotNil(thread);
    dispatch(updateThread(thread));
  };

  const [addThreadCommentMutation] = useMutation<
    IConversationThreadCommentAddMutation,
    IConversationThreadCommentAddMutationVariables
  >(CONVERSATION_THREAD_COMMENT_ADD, {
    onCompleted: handleMutationComplete,
  });

  return useCallback(
    (comment: ICommentInput) => {
      return addThreadCommentMutation({
        variables: {
          input: {
            entity: {
              id: entity.id,
              type: entity.type,
            },
            threadId,
            comment,
          },
        },
      });
    },
    [addThreadCommentMutation, entity, threadId],
  );
};

export default useAddThreadComment;
